import React, { useEffect, useRef, useState } from 'react';
import './TrackVIntegration.css';

interface TrackVIntegrationProps {
  effectType: 'veil' | 'depth' | 'panorama' | 'portals' | 'living';
  sectionId: string;
  children: React.ReactNode;
}

function supportsWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export const TrackVIntegration: React.FC<TrackVIntegrationProps> = ({
  effectType,
  sectionId,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [supportsEffect, setSupportsEffect] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const hasWebGL = supportsWebGL();
    const prefersReduced = prefersReducedMotion();

    setSupportsEffect(hasWebGL);
    setReducedMotion(prefersReduced);

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  if (!mounted) return <div id={sectionId}>{children}</div>;

  const showFallback = !supportsEffect || reducedMotion;

  return (
    <div
      ref={containerRef}
      id={sectionId}
      className={`track-v-integration ${showFallback ? 'track-v-fallback' : ''}`}
      data-effect-type={effectType}
      style={{
        position: 'relative',
        width: '100%',
        touchAction: 'pan-y pinch-zoom',
      }}
    >
      {showFallback && (
        <div className="track-v-fallback-container" style={{ width: '100%' }}>
          {children}
        </div>
      )}

      {!showFallback && (
        <div className="track-v-effect-container" style={{ width: '100%' }}>
          {children}
        </div>
      )}
    </div>
  );
};
